import { useEffect } from 'react'
import { useParams, Navigate, Outlet } from 'react-router-dom'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { Layout } from '@/shell/Layout'
import { PageLoading, EmptyState } from '@/shared/components/EmptyState'

/**
 * Parent route for every org-scoped page (`/org/:orgSlug/...`).
 *
 * Makes the URL the single source of truth for the active org:
 *  - syncs the URL slug into SelectedOrgContext,
 *  - validates the slug against the user's orgs,
 *  - gates rendering of children until context matches the URL, so no child
 *    ever fires an API call against the previously-selected org.
 */
export function OrgLayout() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { selectedSlug, setSelectedSlug, orgs, orgsLoading, orgsRefreshing, orgsError, refetchOrgs } =
    useSelectedOrg()

  // URL drives context. Idempotent - guarded by the inequality.
  useEffect(() => {
    if (orgSlug && orgSlug !== selectedSlug) setSelectedSlug(orgSlug)
  }, [orgSlug, selectedSlug, setSelectedSlug])

  // Org list failed to load - show an error with retry, never redirect (avoids loops).
  if (orgsError) {
    return (
      <Layout title="Organisation">
        <EmptyState
          icon="⚠"
          title="Couldn't load your organisations."
          subtitle={orgsError}
        />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button className="btn" onClick={() => refetchOrgs()}>
            Retry
          </button>
        </div>
      </Layout>
    )
  }

  // Still fetching the org list - wait before validating to avoid a flash-redirect.
  if (orgsLoading && orgs.length === 0) {
    return (
      <Layout title="Organisation">
        <PageLoading />
      </Layout>
    )
  }

  // Unknown slug - redirect to a valid org (computed ONLY from validated orgs to
  // avoid redirect loops on a stale localStorage selection), or to the dashboard.
  const isValid = orgs.some((o) => o.slug === orgSlug)
  // A background refetch is in flight (e.g. an org was just created) - the slug may
  // simply not have landed yet. Wait for it to settle rather than bouncing away.
  if (!isValid && orgsRefreshing) {
    return (
      <Layout title="Organisation">
        <PageLoading />
      </Layout>
    )
  }
  if (!isValid) {
    const fallback = orgs.find((o) => o.slug === selectedSlug)?.slug ?? orgs[0]?.slug
    return <Navigate to={fallback ? `/org/${fallback}/modules` : '/'} replace />
  }

  // Context not yet synced to the URL - gate children for one render so they don't
  // read a stale `selectedOrg` and fetch the wrong tenant.
  if (selectedSlug !== orgSlug) {
    return (
      <Layout title="Organisation">
        <PageLoading />
      </Layout>
    )
  }

  return <Outlet />
}
